import { Button } from "@/components/flexkit/Button";
import { DropdownMenuItem, DropdownMenuSeparator } from "@/components/podkit/dropdown/DropDown";
import { DropdownActions } from "@/components/podkit/dropdown/DropDownActions";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { DeleteRunnerModal, ForceDeleteRunnerModal } from "@/components/runners/DeleteRunnerModal";
import { RenameRunnerModal } from "@/components/runners/RenameRunnerModal";
import { UpgradeRunnerModal } from "@/components/runners/UpgradeRunnerModal";
import { useCurrentMember } from "@/hooks/use-current-member";
import { useResourcePermissions } from "@/hooks/use-resource-permissions";
import { useShareResource } from "@/hooks/use-share-resource";
import { useRunnerActions } from "@/hooks/use-runner-actions";
import type { PlainRunner } from "@/queries/runner-queries";
import { Principal } from "gitpod-next-api/gitpod/v1/identity_pb";
import { OrganizationRole } from "gitpod-next-api/gitpod/v1/organization_pb";
import { RunnerPhase } from "gitpod-next-api/gitpod/v1/runner_pb";
import { RunnerKind, RunnerProvider } from "gitpod-next-api/gitpod/v1/runner_configuration_pb";
import { MoreHorizontalIcon } from "lucide-react";
import { useCallback, useState, type FC } from "react";
import { useNavigate } from "react-router-dom";
import { ResourceType } from "gitpod-next-api/gitpod/v1/event_pb";
import { IconOnaDownload } from "@/assets/icons/IconOnaDownload";

export const RunnerActionsDropdown: FC<{
    runner: PlainRunner;
    button?: React.ReactNode;
    showViewDetails: boolean;
    onDeleteSuccessful?: () => void;
}> = ({ runner, button, showViewDetails, onDeleteSuccessful }) => {
    const navigate = useNavigate();
    const { openShareDialog } = useShareResource();
    const { downloadSupportBundle, isSupportBundleAvailable } = useRunnerActions(runner);

    const { currentMember } = useCurrentMember();
    const { hasPermission } = useResourcePermissions({ enabled: true });

    const [showRenameModal, setShowRenameModal] = useState(false);
    const [showDeletionModal, setShowDeletionModal] = useState(false);
    const [showForceDeleteModal, setShowForceDeleteModal] = useState(false);
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const { toast } = useToast();

    const version = runner.status?.version || "unknown";

    const handleViewDetails = useCallback(() => {
        navigate(`/settings/runners/${runner.runnerId}`);
    }, [navigate, runner.runnerId]);

    const handleCopyId = useCallback(async () => {
        await navigator.clipboard.writeText(runner.runnerId);
        toast({
            title: `Runner ID copied to clipboard`,
            description: runner.runnerId,
        });
    }, [toast, runner.runnerId]);

    const handleCopyVersion = useCallback(async () => {
        await navigator.clipboard.writeText(version);
        toast({
            title: `Runner version copied to clipboard`,
            description: version,
        });
    }, [toast, version]);

    const handleShare = useCallback(() => {
        openShareDialog({
            resourceType: ResourceType.RUNNER,
            resourceId: runner.runnerId,
            resourceName: runner.name,
        });
    }, [runner, openShareDialog]);

    const isCreator =
        runner.creator?.principal === Principal.USER
            ? runner.creator?.id === (currentMember?.userId || "no-member")
            : false;
    const isAdmin = currentMember?.role === OrganizationRole.ADMIN;
    const isManagedRunner = runner.provider === RunnerProvider.MANAGED;

    // Permission checks
    const canUpdateRunner = hasPermission(ResourceType.RUNNER, runner.runnerId, "runner:update");
    const canDeletePermission = hasPermission(ResourceType.RUNNER, runner.runnerId, "runner:delete");
    const canGrantRunner = hasPermission(ResourceType.RUNNER, runner.runnerId, "runner:grant");

    // Maintain backward compatibility with creator logic
    const canEdit = (canUpdateRunner || isCreator) && !isManagedRunner;
    const canDelete = canDeletePermission || isCreator;
    const canShare = canGrantRunner;
    // We don't allow force deletion of managed runners, as this leaks resources.
    const allowForceDelete = runner && runner.provider !== RunnerProvider.MANAGED;
    const canViewDetails = runner.kind === RunnerKind.REMOTE && canEdit;
    const isDeleting = runner.spec?.desiredPhase === RunnerPhase.DELETED;

    const handleViewEnvironments = useCallback(() => {
        navigate(`/settings/environments?runner=${runner.runnerId}`);
    }, [navigate, runner.runnerId]);

    const showEdit = canEdit;
    const showDelete = canDelete && !isDeleting;
    const showForceDelete = canDelete && isDeleting && allowForceDelete;
    const isAWSRunner = runner.provider === RunnerProvider.AWS_EC2;
    const showUpgrade = isAWSRunner && canEdit;

    return (
        // Ensure that clicks and keyboard events in the actions menu or modals don't propagate to parent elements
        <div
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            data-tracking-id="open-runner-actions-dropdown"
        >
            <DropdownActions
                button={
                    button || (
                        <Button
                            variant="ghost"
                            className="size-6 p-0"
                            data-testid="runner-actions-dropdown"
                            aria-label="More actions"
                        >
                            <MoreHorizontalIcon className="size-5 text-content-primary" />
                        </Button>
                    )
                }
                triggerTestId="runner-actions-dropdown-trigger"
            >
                {canViewDetails && showViewDetails && (
                    <DropdownMenuItem
                        onClick={handleViewDetails}
                        data-tracking-id="view-runner-details-runner-actions-dropdown"
                    >
                        View Details
                    </DropdownMenuItem>
                )}
                {isAdmin && (
                    <DropdownMenuItem
                        onClick={handleViewEnvironments}
                        disabled={runner.status?.phase === RunnerPhase.CREATED}
                        title={
                            runner.status?.phase === RunnerPhase.CREATED
                                ? "Available after runner connects"
                                : undefined
                        }
                        data-tracking-id="view-runner-environments-runner-actions-dropdown"
                    >
                        View Environments
                    </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={handleCopyId} data-tracking-id="copy-runner-id-runner-actions-dropdown">
                    Copy ID
                </DropdownMenuItem>
                <DropdownMenuItem
                    onClick={handleCopyVersion}
                    disabled={!runner.status?.version}
                    aria-disabled={!runner.status?.version}
                    title={!runner.status?.version ? "Version not available yet" : undefined}
                    data-tracking-id="copy-runner-version-runner-actions-dropdown"
                >
                    Copy Version
                </DropdownMenuItem>

                {isSupportBundleAvailable && (
                    <DropdownMenuItem
                        data-testid="menu-item-download-support-bundle"
                        onClick={downloadSupportBundle}
                        LeadingIcon={IconOnaDownload}
                        data-tracking-id="download-support-bundle"
                    >
                        Download support bundle
                    </DropdownMenuItem>
                )}

                {(showEdit || showDelete || showForceDelete || showUpgrade) && (
                    <>
                        <DropdownMenuSeparator className="bg-content-tertiary/20" />
                        {canShare && (
                            <DropdownMenuItem
                                onClick={handleShare}
                                data-testid="runner-actions-dropdown-share"
                                data-tracking-id="share-runner-runner-actions-dropdown"
                            >
                                Share runner
                            </DropdownMenuItem>
                        )}
                        {showEdit && (
                            <DropdownMenuItem
                                onClick={() => setShowRenameModal(true)}
                                data-tracking-id="rename-runner-runner-actions-dropdown"
                            >
                                Rename
                            </DropdownMenuItem>
                        )}
                        {showUpgrade && (
                            <DropdownMenuItem
                                onClick={() => setShowUpgradeModal(true)}
                                data-testid="runner-actions-dropdown-upgrade"
                                data-tracking-id="upgrade-runner-runner-actions-dropdown"
                            >
                                Upgrade runner
                            </DropdownMenuItem>
                        )}
                        {showDelete && (
                            // Not yet deleted, show regular delete.
                            <DropdownMenuItem
                                className="text-red-500 focus:text-red-500"
                                onClick={() => {
                                    setShowDeletionModal(true);
                                }}
                                data-tracking-id="delete-runner-runner-actions-dropdown"
                            >
                                Delete
                            </DropdownMenuItem>
                        )}
                        {showForceDelete && (
                            // Already deleted, show force delete.
                            <DropdownMenuItem
                                className="text-red-500 focus:text-red-500"
                                onClick={() => {
                                    setShowForceDeleteModal(true);
                                }}
                                data-tracking-id="force-delete-runner-runner-actions-dropdown"
                            >
                                Force Delete
                            </DropdownMenuItem>
                        )}
                    </>
                )}
            </DropdownActions>

            <DeleteRunnerModal
                open={showDeletionModal}
                runner={runner}
                onClose={() => setShowDeletionModal(false)}
                onDeleteSuccessful={onDeleteSuccessful}
            />
            <ForceDeleteRunnerModal
                open={showForceDeleteModal}
                runner={runner}
                onClose={() => setShowForceDeleteModal(false)}
                onDeleteSuccessful={onDeleteSuccessful}
            />
            <RenameRunnerModal open={showRenameModal} runner={runner} onClose={() => setShowRenameModal(false)} />
            <UpgradeRunnerModal open={showUpgradeModal} runner={runner} onClose={() => setShowUpgradeModal(false)} />
        </div>
    );
};
